
import React from 'react';
import { Diamond, Crown, Star, Trophy } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const services = [
  {
    title: "Estate Cleaning",
    description: "Comprehensive cleaning solutions tailored for luxury residences and estates.",
    icon: Diamond,
    tasks: [
      "Marble and Natural Stone Cleaning and Care",
      "High-end fabric care",
      "Custom Wood Surface Cleaning and Care",
      "Delicate Antique Surface Cleaning",
      "Crystal chandelier cleaning",
      "Premium surface treatments"
    ]
  },
  {
    title: "Signature Services",
    description: "Personalized cleaning services designed for your prestigious property.",
    icon: Crown,
    tasks: [
      "Customized cleaning schedules",
      "White glove service",
      "Fine art and decor cleaning",
      "High-end appliance cleaning",
      "Premium surface cleaning",
      "Exclusive cleaning products"
    ]
  },
  {
    title: "Luxury Property Services",
    description: "Specialized cleaning services for property transitions and special occasions.",
    icon: Star,
    tasks: [
      "Estate showing preparation",
      "Seasonal residence preparation",
      "Special event setup and cleanup",
      "Wine cellar cleaning",
      "Home theater cleaning",
      "Smart home surface cleaning"
    ]
  },
  {
    title: "Bespoke Cleaning Services",
    description: "We craft personalized cleaning programs designed to preserve the beauty, refinement, and privacy of your prestigious residence.",
    icon: Trophy,
    tasks: [
      "Private spa cleaning",
      "Indoor pool area cleaning",
      "Home gym sanitization",
      "Custom wardrobe cleaning",
      "Wine room cleaning",
      "Exotic material cleaning"
    ]
  }
];

const cleaningDuties = [
  {
    category: "Kitchen Cleaning",
    duties: [
      "Countertop and backsplash detailed cleaning",
      "Appliance exterior and interior cleaning",
      "Cabinet front cleaning and polishing",
      "Sink and fixtures sanitizing and polishing",
      "Floor vacuuming, sweeping, and mopping",
      "Pantry organization and cleaning"
    ]
  },
  {
    category: "Living Areas",
    duties: [
      "Custom dusting of all surfaces and decor",
      "Upholstery vacuuming and spot treatment",
      "Wood and specialty floor cleaning",
      "Light fixture and ceiling fan dusting",
      "Window treatment dusting",
      "Art and mirror careful cleaning"
    ]
  },
  {
    category: "Bedrooms",
    duties: [
      "Premium linen changing and bed making",
      "Closet organization and cleaning",
      "Under-bed cleaning",
      "Nightstand and dresser dusting",
      "Lamp and decor dusting",
      "Floor vacuuming and appropriate treatment"
    ]
  },
  {
    category: "Bathrooms",
    duties: [
      "Shower, tub, and sink deep cleaning",
      "Toilet sanitizing and cleaning",
      "Mirror and glass surface streak-free cleaning",
      "Countertop detailed cleaning",
      "Floor sanitizing and cleaning",
      "Luxury amenity organization"
    ]
  }
];

const Services = () => {
  return (
    <section id="services" className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="w-full h-full" 
          style={{
            backgroundImage: "linear-gradient(90deg, #004080 1px, transparent 1px), linear-gradient(180deg, #004080 1px, transparent 1px)",
            backgroundSize: "40px 40px"
          }}>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4 font-serif">Bespoke Cleaning Services for Luxury Homes</h2>
          <div className="w-24 h-1 bg-gold mx-auto mb-6"></div>
          <p className="text-gray-800 max-w-2xl mx-auto">
            We craft personalized cleaning programs designed to preserve the beauty, refinement, and privacy of your prestigious residence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <AspectRatio ratio={16/9} className="bg-gray-100 overflow-hidden rounded-lg border border-gray-200 shadow-sm">
            <img 
              src="/lovable-uploads/cc90b2f7-f78e-4ade-9d3c-d46ddf60c9f7.png"
              alt="Grand entrance with ornate chandelier"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
          </AspectRatio>
          <AspectRatio ratio={16/9} className="bg-gray-100 overflow-hidden rounded-lg border border-gray-200 shadow-sm">
            <img 
              src="/lovable-uploads/76277cfe-2df0-452e-9316-ba5b795de267.png"
              alt="Elegant bathroom with luxury finishes"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
          </AspectRatio>
          <AspectRatio ratio={16/9} className="bg-gray-100 overflow-hidden rounded-lg border border-gray-200 shadow-sm">
            <img 
              src="/lovable-uploads/4fc0a154-7807-4f9d-93a4-61531d075ff9.png"
              alt="Sophisticated living room with premium furniture"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
          </AspectRatio>
        </div>

        {/* Service Types Section */}
        <div className="mb-16">
          <h3 className="text-2xl md:text-3xl font-bold text-navy mb-8 text-center font-serif">Our Premium Cleaning Services</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-navy/10 rounded-full flex items-center justify-center mr-4">
                    <service.icon className="h-6 w-6 text-navy" />
                  </div>
                  <h4 className="text-lg font-bold text-navy font-serif">{service.title}</h4>
                </div>
                <p className="text-gray-700 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.tasks.map((task, taskIndex) => (
                    <li key={taskIndex} className="flex items-start">
                      <span className="inline-block w-1.5 h-1.5 bg-gold rounded-full mt-2 mr-2"></span>
                      <span className="text-gray-700">{task}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Cleaning Duties Section */}
        <div className="mb-16">
          <h3 className="text-2xl md:text-3xl font-bold text-navy mb-8 text-center font-serif">Our Cleaning Duties</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cleaningDuties.map((category, index) => (
              <Card key={index} className="border-none shadow-lg bg-white/80 backdrop-blur-sm hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-navy font-serif">{category.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {category.duties.map((duty, dutyIndex) => (
                      <li key={dutyIndex} className="flex items-start">
                        <span className="inline-block w-2 h-2 bg-gold rounded-full mt-2 mr-3"></span>
                        <span className="text-gray-700">{duty}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-lg text-gray-800 font-medium mb-6">
            Looking for a Personalized Home Cleaning Experience?
          </p>
          <a href="#contact" className="inline-block px-8 py-3 bg-navy hover:bg-navy/90 text-white rounded-md font-medium transition-colors duration-300 shadow-lg hover:shadow-xl">
            Schedule a Private Consultation
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
